import * as THREE from 'three';
import particleVert from '../../glsl/particle_vert.glsl';
import particleFrag from '../../glsl/particle_frag.glsl';
import { PARTICLES, RENDERING } from '../core/SimConfig.js';
import { rand, srand, randSphere } from '../utils.js';

// ── Type lookup ──────────────────────────────────────────────────────────────

const TYPES = PARTICLES.types;
const DEBRIS = PARTICLES.debris;

// ── ParticleSystem ───────────────────────────────────────────────────────────

export default class ParticleSystem {
  /**
   * @param {{ scene: THREE.Scene, atlas: THREE.Texture }} opts
   */
  constructor({ scene, atlas }) {
    this.scene = scene;
    const N = PARTICLES.maxCount;
    this.maxCount = N;

    // ── Struct-of-Arrays particle pool ───────────────────────────────
    this.posX       = new Float32Array(N);
    this.posY       = new Float32Array(N);
    this.posZ       = new Float32Array(N);
    this.velX       = new Float32Array(N);
    this.velY       = new Float32Array(N);
    this.velZ       = new Float32Array(N);
    this.age        = new Float32Array(N);
    this.lifetime   = new Float32Array(N);
    this.startSize  = new Float32Array(N);
    this.endSize    = new Float32Array(N);
    this.startAlpha = new Float32Array(N);
    this.endAlpha   = new Float32Array(N);
    this.friction   = new Float32Array(N);
    this.tile       = new Float32Array(N);
    this.emissive   = new Float32Array(N);
    this.colorR     = new Float32Array(N);
    this.colorG     = new Float32Array(N);
    this.colorB     = new Float32Array(N);
    this.gravity    = new Float32Array(N);
    this.rotation   = new Float32Array(N);  // random billboard rotation (radians)
    this.drift      = new Uint8Array(N);   // 1 = affected by wind
    this.alive      = new Uint8Array(N);   // 0 = dead
    this.isEmissive = new Uint8Array(N);   // 0 = normal blend, 1 = additive

    // ── Free-list (stack) for O(1) alloc ─────────────────────────────
    this._freeList = new Uint16Array(N);
    this._freeCount = N;
    for (let i = 0; i < N; i++) this._freeList[i] = N - 1 - i; // top of stack = 0

    // ── Texture (provided by AssetManager) ─────────────────────────
    this._atlas = atlas;

    // ── Shared geometry: unit quad ───────────────────────────────────
    this._quadGeo = new THREE.PlaneGeometry(1, 1, 1, 1);

    // ── Two instanced meshes: normal-blend and additive ─────────────
    this._normalMesh = this._createMesh(N, false);
    this._additiveMesh = this._createMesh(N, true);
    scene.add(this._normalMesh);
    scene.add(this._additiveMesh);
    // Both sprite meshes, for App to move on/off the FX overlay layer (see
    // App._syncFXOverlay) — held as one array so that runs allocation-free.
    // Debris is deliberately not in here: it's opaque and writes depth, so the
    // cloud pass already composites against it correctly.
    this.spriteMeshes = [this._normalMesh, this._additiveMesh];

    // ── Per-frame write cursors ──────────────────────────────────────
    this._normalCount = 0;
    this._additiveCount = 0;

    // ── Debris subsystem ─────────────────────────────────────────────
    this._initDebris();
  }

  // ── Debris init ─────────────────────────────────────────────────────────────

  _initDebris() {
    const N = DEBRIS.poolSize;
    const S = DEBRIS.shapeCount;

    // SoA pool — position, velocity, angular velocity, quaternion, lifetime, etc.
    this.dPosX     = new Float32Array(N);
    this.dPosY     = new Float32Array(N);
    this.dPosZ     = new Float32Array(N);
    this.dVelX     = new Float32Array(N);
    this.dVelY     = new Float32Array(N);
    this.dVelZ     = new Float32Array(N);
    this.dAvX      = new Float32Array(N);
    this.dAvY      = new Float32Array(N);
    this.dAvZ      = new Float32Array(N);
    this.dQx       = new Float32Array(N);
    this.dQy       = new Float32Array(N);
    this.dQz       = new Float32Array(N);
    this.dQw       = new Float32Array(N);
    this.dAge      = new Float32Array(N);
    this.dLife     = new Float32Array(N);
    this.dScale    = new Float32Array(N);
    this.dColR     = new Float32Array(N);
    this.dColG     = new Float32Array(N);
    this.dColB     = new Float32Array(N);
    this.dShape    = new Uint8Array(N);
    this.dAlive    = new Uint8Array(N);

    // Free-list (stack)
    this._dFree = new Uint16Array(N);
    this._dFreeCount = N;
    for (let i = 0; i < N; i++) this._dFree[i] = N - 1 - i;

    // Pre-bake random flat polygon geometries
    this._debrisGeos = [];
    for (let s = 0; s < S; s++) {
      this._debrisGeos.push(this._buildDebrisGeometry());
    }

    // Shared unlit material — instanceColor multiplies material.color (white)
    this._debrisMat = new THREE.MeshBasicMaterial({
      color: 0xffffff,
      side: THREE.DoubleSide,
      toneMapped: true,
    });

    // One InstancedMesh per shape variant
    this._debrisMeshes = [];
    for (let s = 0; s < S; s++) {
      const m = new THREE.InstancedMesh(this._debrisGeos[s], this._debrisMat, N);
      m.frustumCulled = false;
      m.count = 0;
      m.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
      m.instanceColor = new THREE.InstancedBufferAttribute(new Float32Array(N * 3), 3);
      m.instanceColor.setUsage(THREE.DynamicDrawUsage);
      this.scene.add(m);
      this._debrisMeshes.push(m);
    }

    this._debrisAliveCount = 0;
  }

  /** Random irregular flat polygon (3-5 verts) on a unit disk, fan-triangulated. */
  _buildDebrisGeometry() {
    const verts = 3 + Math.floor(Math.random() * 3); // 3, 4, or 5
    const angles = new Float32Array(verts);
    for (let i = 0; i < verts; i++) angles[i] = Math.random() * Math.PI * 2;
    angles.sort();

    const positions = new Float32Array(verts * 3);
    for (let i = 0; i < verts; i++) {
      const r = 0.5 + Math.random() * 0.5;
      positions[i * 3]     = Math.cos(angles[i]) * r;
      positions[i * 3 + 1] = Math.sin(angles[i]) * r;
      positions[i * 3 + 2] = 0;
    }

    const indices = [];
    for (let i = 1; i < verts - 1; i++) indices.push(0, i, i + 1);

    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    geo.setIndex(indices);
    geo.computeBoundingSphere();
    return geo;
  }

  // ── Mesh factory ───────────────────────────────────────────────────────────

  _createMesh(maxInstances, additive) {
    const geo = new THREE.InstancedBufferGeometry();
    geo.index = this._quadGeo.index;
    geo.setAttribute('position', this._quadGeo.getAttribute('position'));
    geo.setAttribute('uv', this._quadGeo.getAttribute('uv'));

    // Instance attributes — pre-allocate max size, update count per frame
    const aOffset      = new THREE.InstancedBufferAttribute(new Float32Array(maxInstances * 3), 3);
    const aScaleAlpha  = new THREE.InstancedBufferAttribute(new Float32Array(maxInstances * 2), 2);
    const aTileEmissive = new THREE.InstancedBufferAttribute(new Float32Array(maxInstances * 2), 2);
    const aColor       = new THREE.InstancedBufferAttribute(new Float32Array(maxInstances * 3), 3);
    const aRotation    = new THREE.InstancedBufferAttribute(new Float32Array(maxInstances), 1);

    aOffset.setUsage(THREE.DynamicDrawUsage);
    aScaleAlpha.setUsage(THREE.DynamicDrawUsage);
    aTileEmissive.setUsage(THREE.DynamicDrawUsage);
    aColor.setUsage(THREE.DynamicDrawUsage);
    aRotation.setUsage(THREE.DynamicDrawUsage);

    geo.setAttribute('aOffset', aOffset);
    geo.setAttribute('aScaleAlpha', aScaleAlpha);
    geo.setAttribute('aTileEmissive', aTileEmissive);
    geo.setAttribute('aColor', aColor);
    geo.setAttribute('aRotation', aRotation);

    const mat = new THREE.RawShaderMaterial({
      uniforms: {
        uAtlas:         { value: this._atlas },
        uAtlasSize:     { value: PARTICLES.atlasSize },
        uLogDepthBufFC: { value: 2.0 / (Math.log(RENDERING.farPlane + 1.0) / Math.LN2) },
        uNearFadeStart: { value: PARTICLES.nearFadeStart },
        uNearFadeEnd:   { value: PARTICLES.nearFadeEnd },
        uSkyTop:        { value: new THREE.Vector3(1, 1, 1) },
        uSkyBottom:     { value: new THREE.Vector3(0.3, 0.3, 0.3) },
        uEffectBrightness: { value: 1.0 },
      },
      glslVersion: THREE.GLSL3,
      vertexShader: `
        precision highp float;
        ${particleVert}
      `,
      fragmentShader: `
        precision highp float;
        ${particleFrag}
      `,
      transparent: true,
      depthWrite: false,
      depthTest: true,
      blending: THREE.CustomBlending,
      blendSrc: THREE.OneFactor,
      blendDst: additive ? THREE.OneFactor : THREE.OneMinusSrcAlphaFactor,
      side: THREE.DoubleSide,
    });

    const mesh = new THREE.Mesh(geo, mat);
    mesh.frustumCulled = false;
    // Start with zero visible instances
    geo.instanceCount = 0;
    return mesh;
  }

  // ── Pool allocator ─────────────────────────────────────────────────────────

  _alloc() {
    if (this._freeCount === 0) return -1;
    return this._freeList[--this._freeCount];
  }

  _free(i) {
    this._freeList[this._freeCount++] = i;
  }

  // ── Low-level emit ─────────────────────────────────────────────────────────

  /**
   * Emit a single particle.
   * @param {string} typeName — key in PARTICLES.types
   * @param {number} x, y, z — world position
   * @param {number} vx, vy, vz — initial velocity
   * @param {object} [overrides] — optional per-field overrides
   */
  _emit(typeName, x, y, z, vx, vy, vz, overrides) {
    const slot = this._alloc();
    if (slot === -1) return; // pool exhausted

    const t = TYPES[typeName];

    this.posX[slot] = x;
    this.posY[slot] = y;
    this.posZ[slot] = z;
    this.velX[slot] = vx;
    this.velY[slot] = vy;
    this.velZ[slot] = vz;
    this.age[slot]  = 0;

    this.lifetime[slot]   = rand(t.lifetime[0], t.lifetime[1]);
    this.startSize[slot]  = overrides?.startSize ?? rand(t.startSize[0], t.startSize[1]);
    this.endSize[slot]    = overrides?.endSize   ?? rand(t.endSize[0], t.endSize[1]);
    this.startAlpha[slot] = overrides?.startAlpha ?? t.startAlpha;
    this.endAlpha[slot]   = overrides?.endAlpha   ?? t.endAlpha;
    this.friction[slot]   = overrides?.friction   ?? t.friction;
    this.tile[slot]       = overrides?.tile        ?? t.tile;
    this.emissive[slot]   = overrides?.emissive    ?? t.emissive;
    this.gravity[slot]    = overrides?.gravity     ?? t.gravity;
    this.rotation[slot]   = Math.random() * Math.PI * 2;
    this.drift[slot]      = t.drift ? 1 : 0;
    this.alive[slot]      = 1;
    this.isEmissive[slot] = t.emissive > 0 ? 1 : 0;

    this.colorR[slot] = overrides?.r ?? t.color[0];
    this.colorG[slot] = overrides?.g ?? t.color[1];
    this.colorB[slot] = overrides?.b ?? t.color[2];
  }

  // ── High-level effect spawners ─────────────────────────────────────────────

  /**
   * Muzzle smoke from a single bullet firing.
   * @param {number} x,y,z — gun world position
   * @param {number} vx,vy,vz — aircraft velocity
   * @param {number} dx,dy,dz — gun barrel direction (unit)
   */
  spawnMuzzleFlash(x, y, z, vx, vy, vz, dx, dy, dz) {
    const spread = 0.3;       // random sphere radius for position offset
    const forwardSpeed = 8;   // extra speed along barrel direction

    // muzzleSmokeA
    const oA = randSphere(Math.random() * spread);
    this._emit('muzzleSmokeA',
      x + oA.x, y + oA.y, z + oA.z,
      vx + dx * forwardSpeed + srand() * 1,
      vy + dy * forwardSpeed + srand() * 1,
      vz + dz * forwardSpeed + srand() * 1,
    );

    // muzzleSmokeB
    const oB = randSphere(Math.random() * spread);
    this._emit('muzzleSmokeB',
      x + oB.x, y + oB.y, z + oB.z,
      vx + dx * forwardSpeed + srand() * 1,
      vy + dy * forwardSpeed + srand() * 1,
      vz + dz * forwardSpeed + srand() * 1,
    );
  }

  /**
   * Dirt and dust kicking up.
   * @param {number} scale — size multiplier (default 1)
   */
  spawnDirtImpact(x, y, z, scale = 1) {
    // Dust small
    for (let i = 0; i < 3; i++) {
      this._emit('dustSmall', x, y + rand(0, 1), z,
        srand() * 2 * scale, rand(7, 14) * scale, srand() * 2 * scale);
    }

    // Dust large
    this._emit('dustLarge', x, y + rand(0, 2), z,
        srand() * 2 * scale, rand(1, 3) * scale, srand() * 2 * scale);
  }

  /**
   * 30mm incendiary explosion
   * @param {number} scale — size multiplier (default 1)
   */
  spawnIncendiaryImpact(x, y, z, scale = 1) {
    // White smoke
    this._emit('whiteSmoke', x, y, z,
      0, 0, 0, {});

    // Flash
    this._emit('flash', x, y, z,
      0, 0, 0,
      { startSize: 4 * scale, endSize: 10 * scale });

    // Sparks
    this._emit('sparks', x, y, z,
      0, 0, 0,
      { startSize: 0.5 * scale, endSize: 3 * scale });
  }

  /**
   * Emit a single white smoke puff — call each frame for a continuous trail.
   */
  spawnWhiteSmokeTrail(x, y, z, vx, vy, vz) {
    const j = randSphere(2);
    this._emit('whiteSmoke', x, y, z,
      vx * 0.1 + j.x,
      vy * 0.1 + j.y,
      vz * 0.1 + j.z,
      {});
  }

  /**
   * Emit a single black smoke puff — call each frame for a continuous trail.
   */
  spawnBlackSmokeTrail(x, y, z, vx, vy, vz) {
    const j = randSphere(2);
    this._emit('blackSmoke', x, y, z,
      vx * 0.1 + j.x,
      vy * 0.1 + j.y,
      vz * 0.1 + j.z,
      {});
  }

  /**
   * Emit a fire and black smoke — call each frame for a continuous trail.
   */
  spawnFireSmokeTrail(x, y, z, vx, vy, vz) {
    const j1 = randSphere(2);
    this._emit('fire', x, y, z,
      vx * 0.1 + j1.x,
      vy * 0.1 + j1.y,
      vz * 0.1 + j1.z,
      {});
    const j2 = randSphere(0.5);
    this._emit('blackSmoke', x, y, z,
      vx * 0.25 + j2.x,
      vy * 0.25 + j2.y,
      vz * 0.25 + j2.z,
      {});
  }

  /**
   * Emit rising fire and rising smoke — call each frame for a continuous
   * trail from a burning wreck sitting on the ground. Identical to
   * spawnFireSmokeTrail, but uses the negative-gravity `groundFire` /
   * `groundSmoke` types so the column reads as a buoyant heat plume.
   */
  spawnGroundFireSmokeTrail(x, y, z, vx, vy, vz) {
    const j1 = randSphere(2);
    this._emit('groundFire', x, y, z,
      vx * 0.1 + j1.x,
      vy * 0.1 + j1.y,
      vz * 0.1 + j1.z,
      {});
    const j2 = randSphere(0.5);
    this._emit('groundSmoke', x, y, z,
      vx * 0.5 + j2.x,
      vy * 0.5 + j2.y,
      vz * 0.5 + j2.z,
      {});
  }

  /**
   * Debris only
   */
  spawnShrapnel(x, y, z, opts) {
    const size         = opts?.size         ?? 1;
    const debrisCount  = opts?.debrisCount  ?? 6;

    // Small inert tumbling debris
    for (let i = 0; i < debrisCount; i++)  this._spawnDebris(x, y, z, size);
  }

  /**
   * Big explosion — central fireball, smoke, sparks, and tumbling shrapnel.
   *
   * @param {number} x,y,z — world position
   * @param {object} [opts]
   * @param {number} [opts.size=1]          — overall size multiplier
   * @param {number} [opts.debrisCount=24]  — small inert tumbling chunks
   */
  spawnExplosion(x, y, z, opts) {
    const size         = opts?.size         ?? 1;
    const debrisCount  = opts?.debrisCount  ?? 24;

    // Bright central flash
    this._emit('flash', x, y, z, 0, 0, 0,
      { startSize: 16 * size, endSize: 24 * size });

    // Fireball — several offset fire puffs with outward drift
    for (let i = 0; i < 16; i++) {
      const o = randSphere(2.5 * size);
      this._emit('fire', x + o.x, y + o.y, z + o.z,
        o.x * 1.5, o.y * 1.5 + 1, o.z * 1.5,
        { startSize: 6 * size, endSize: 12 * size });
    }

    // Black smoke cloud
    for (let i = 0; i < 16; i++) {
      const o = randSphere(4 * size);
      this._emit('blackSmoke', x + o.x, y + o.y, z + o.z,
        o.x * 0.5, o.y * 0.5 + 2, o.z * 0.5);
    }

    // Dust kick-up
    for (let i = 0; i < 1; i++) {
      this._emit('dustLarge', x, y, z,
        srand() * 4 * size, rand(2, 6) * size, srand() * 4 * size);
    }

    // Small inert tumbling debris
    for (let i = 0; i < debrisCount; i++)  this._spawnDebris(x, y, z, size);
  }

  /**
   * Scattered ground dust from a blast sitting on the deck — a loose ring of
   * puffs thrown out around the explosion, on top of the single central
   * kick-up spawnExplosion does. A vehicle dies on the ground, so the blast
   * lifts a patch of dirt around itself, not just a fireball.
   *
   * Pass a terrain collider and each puff is placed on the ground under it, so
   * the scatter follows a slope instead of hanging off it.
   *
   * @param {number} x,y,z — blast world position
   * @param {object} [opts]
   * @param {number} [opts.size=1]  — overall size + radius multiplier
   * @param {number} [opts.count]   — puffs (default PARTICLES.groundDustBurst.count)
   * @param {object} [opts.terrainCollider] — optional, for per-puff ground height
   */
  spawnGroundDustBurst(x, y, z, opts) {
    const cfg     = PARTICLES.groundDustBurst;
    const size    = opts?.size ?? 1;
    const count   = opts?.count ?? cfg.count;
    const radius  = cfg.radius * size;
    const terrain = opts?.terrainCollider ?? null;
    const t       = TYPES.dustLarge;

    for (let i = 0; i < count; i++) {
      // Uniform angle, but a plain-random radius — which biases the scatter
      // inward, since area grows as r². That's what a blast pattern looks
      // like: dense at the seat, thinning toward the edge.
      const a = Math.random() * Math.PI * 2;
      const r = Math.random() * radius;
      const ca = Math.cos(a), sa = Math.sin(a);
      const px = x + ca * r;
      const pz = z + sa * r;
      // getHeightAt is null outside the streamed tiles — fall back to the
      // blast's own Y rather than dropping the puff to sea level.
      const py = (terrain?.getHeightAt(px, pz) ?? y) + Math.random() * cfg.riseHeight;

      // Outward drift, strongest at the seat of the blast and gone by the rim.
      const outward = cfg.outwardSpeed * (1 - r / radius) * size;
      // Each puff gets its own size so the scatter reads as separate kick-ups
      // rather than copies of the central plume.
      const s = size * rand(cfg.sizeScale[0], cfg.sizeScale[1]);

      this._emit('dustLarge', px, py, pz,
        ca * outward + srand() * cfg.jitterSpeed,
        rand(cfg.riseSpeed[0], cfg.riseSpeed[1]) * size,
        sa * outward + srand() * cfg.jitterSpeed,
        {
          startSize: rand(t.startSize[0], t.startSize[1]) * s,
          endSize:   rand(t.endSize[0],   t.endSize[1])   * s,
        },
      );
    }
  }

  /** Allocate one debris piece. */
  _spawnDebris(x, y, z, size) {
    if (this._dFreeCount === 0) return;
    const i = this._dFree[--this._dFreeCount];

    const dir = randSphere(1);
    const speed = rand(8, 16) * size;
    const off = randSphere(0.5 * size);

    this.dPosX[i] = x + off.x;
    this.dPosY[i] = y + off.y;
    this.dPosZ[i] = z + off.z;

    this.dVelX[i] = dir.x * speed;
    this.dVelY[i] = dir.y * speed + rand(3, 8) * size;
    this.dVelZ[i] = dir.z * speed;

    this.dAvX[i] = srand() * 14;
    this.dAvY[i] = srand() * 14;
    this.dAvZ[i] = srand() * 14;

    // Random initial orientation (uniform on SO(3))
    const u1 = Math.random(), u2 = Math.random(), u3 = Math.random();
    const s1 = Math.sqrt(1 - u1), s2 = Math.sqrt(u1);
    this.dQx[i] = s1 * Math.sin(2 * Math.PI * u2);
    this.dQy[i] = s1 * Math.cos(2 * Math.PI * u2);
    this.dQz[i] = s2 * Math.sin(2 * Math.PI * u3);
    this.dQw[i] = s2 * Math.cos(2 * Math.PI * u3);

    this.dAge[i]  = 0;
    this.dLife[i] = rand(1.5, 3.0);
    this.dScale[i] = rand(0.25, 0.65) * size;
    this.dShape[i] = Math.floor(Math.random() * this._debrisMeshes.length);
    this.dAlive[i] = 1;

    const j = (Math.random() * 2 - 1) * DEBRIS.colorJitter;
    this.dColR[i] = Math.max(0, DEBRIS.color[0] + j);
    this.dColG[i] = Math.max(0, DEBRIS.color[1] + j);
    this.dColB[i] = Math.max(0, DEBRIS.color[2] + j);
  }

  // ── Debris update ───────────────────────────────────────────────────────────

  /**
   * Integrate all live debris and write per-instance matrices/colors.
   */
  _updateDebris(dt) {
    const N = this.dAlive.length;
    const meshes = this._debrisMeshes;
    const numShapes = meshes.length;

    // Per-shape write cursors (local instance index within each InstancedMesh)
    const cursors = new Array(numShapes);
    for (let s = 0; s < numShapes; s++) cursors[s] = 0;

    // Drag (frame-rate-corrected exponential decay)
    const linDrag = Math.exp(-DEBRIS.drag * dt);
    const angDrag = Math.exp(-DEBRIS.angularDrag * dt);
    const g = DEBRIS.gravity * dt;
    const halfDt = 0.5 * dt;

    let alive = 0;

    for (let i = 0; i < N; i++) {
      if (!this.dAlive[i]) continue;

      this.dAge[i] += dt;
      if (this.dAge[i] >= this.dLife[i]) {
        this.dAlive[i] = 0;
        this._dFree[this._dFreeCount++] = i;
        continue;
      }

      // Linear: gravity, drag, integrate
      let vx = this.dVelX[i], vy = this.dVelY[i] - g, vz = this.dVelZ[i];
      vx *= linDrag; vy *= linDrag; vz *= linDrag;
      this.dVelX[i] = vx; this.dVelY[i] = vy; this.dVelZ[i] = vz;
      const px = (this.dPosX[i] += vx * dt);
      const py = (this.dPosY[i] += vy * dt);
      const pz = (this.dPosZ[i] += vz * dt);

      // Angular: integrate quaternion via q' = 0.5 * ω * q, then renormalize
      const wx = this.dAvX[i], wy = this.dAvY[i], wz = this.dAvZ[i];
      let qx = this.dQx[i], qy = this.dQy[i], qz = this.dQz[i], qw = this.dQw[i];
      const dqx = halfDt * ( wx * qw + wy * qz - wz * qy);
      const dqy = halfDt * (-wx * qz + wy * qw + wz * qx);
      const dqz = halfDt * ( wx * qy - wy * qx + wz * qw);
      const dqw = halfDt * (-wx * qx - wy * qy - wz * qz);
      qx += dqx; qy += dqy; qz += dqz; qw += dqw;
      const invMag = 1 / Math.sqrt(qx * qx + qy * qy + qz * qz + qw * qw);
      qx *= invMag; qy *= invMag; qz *= invMag; qw *= invMag;
      this.dQx[i] = qx; this.dQy[i] = qy; this.dQz[i] = qz; this.dQw[i] = qw;
      this.dAvX[i] *= angDrag; this.dAvY[i] *= angDrag; this.dAvZ[i] *= angDrag;

      // Compose per-instance matrix directly into the shape's instanceMatrix
      // buffer (column-major 4x4). Avoids Matrix4/Quaternion allocations.
      const s = this.dScale[i];
      const xx = qx * qx, yy = qy * qy, zz = qz * qz;
      const xy = qx * qy, xz = qx * qz, yz = qy * qz;
      const wx2 = qw * qx, wy2 = qw * qy, wz2 = qw * qz;

      const shapeIdx = this.dShape[i];
      const mesh = meshes[shapeIdx];
      const localIdx = cursors[shapeIdx]++;
      const mArr = mesh.instanceMatrix.array;
      const cArr = mesh.instanceColor.array;
      const m = localIdx * 16;
      const c = localIdx * 3;

      mArr[m]      = (1 - 2 * (yy + zz)) * s;
      mArr[m + 1]  = (2 * (xy + wz2)) * s;
      mArr[m + 2]  = (2 * (xz - wy2)) * s;
      mArr[m + 3]  = 0;
      mArr[m + 4]  = (2 * (xy - wz2)) * s;
      mArr[m + 5]  = (1 - 2 * (xx + zz)) * s;
      mArr[m + 6]  = (2 * (yz + wx2)) * s;
      mArr[m + 7]  = 0;
      mArr[m + 8]  = (2 * (xz + wy2)) * s;
      mArr[m + 9]  = (2 * (yz - wx2)) * s;
      mArr[m + 10] = (1 - 2 * (xx + yy)) * s;
      mArr[m + 11] = 0;
      mArr[m + 12] = px;
      mArr[m + 13] = py;
      mArr[m + 14] = pz;
      mArr[m + 15] = 1;

      cArr[c]     = this.dColR[i];
      cArr[c + 1] = this.dColG[i];
      cArr[c + 2] = this.dColB[i];

      alive++;
    }

    // Update each mesh's count and flag buffers
    for (let s = 0; s < numShapes; s++) {
      const mesh = meshes[s];
      const n = cursors[s];
      mesh.count = n;
      if (n > 0) {
        mesh.instanceMatrix.needsUpdate = true;
        mesh.instanceColor.needsUpdate = true;
      }
    }

    this._debrisAliveCount = alive;
  }

  // ── Per-frame update ───────────────────────────────────────────────────────

  /**
   * @param {number} dt
   * @param {THREE.Camera} camera
   * @param {THREE.Vector3} [wind] — world-space wind velocity
   */
  update(dt, camera, wind) {
    this._updateDebris(dt);

    const windX = wind ? wind.x : 0;
    const windY = wind ? wind.y : 0;
    const windZ = wind ? wind.z : 0;

    // Attribute buffer refs
    const nGeo = this._normalMesh.geometry;
    const aGeo = this._additiveMesh.geometry;
    const nOff = nGeo.getAttribute('aOffset').array;
    const nSA  = nGeo.getAttribute('aScaleAlpha').array;
    const nTE  = nGeo.getAttribute('aTileEmissive').array;
    const nCol = nGeo.getAttribute('aColor').array;
    const nRot = nGeo.getAttribute('aRotation').array;
    const aOff = aGeo.getAttribute('aOffset').array;
    const aSA  = aGeo.getAttribute('aScaleAlpha').array;
    const aTE  = aGeo.getAttribute('aTileEmissive').array;
    const aCol = aGeo.getAttribute('aColor').array;
    const aRot = aGeo.getAttribute('aRotation').array;

    let ni = 0; // normal instance write cursor
    let ai = 0; // additive instance write cursor

    for (let i = 0; i < this.maxCount; i++) {
      if (!this.alive[i]) continue;

      // Advance age
      this.age[i] += dt;
      if (this.age[i] >= this.lifetime[i]) {
        this.alive[i] = 0;
        this._free(i);
        continue;
      }

      // Normalized lifetime 0..1
      const t = this.age[i] / this.lifetime[i];

      // Apply friction (exponential damping)
      const f = Math.pow(this.friction[i], dt * 60); // normalize to 60fps
      this.velX[i] *= f;
      this.velY[i] *= f;
      this.velZ[i] *= f;

      // Wind drift
      if (this.drift[i]) {
        this.velX[i] += windX * dt;
        this.velY[i] += windY * dt;
        this.velZ[i] += windZ * dt;
      }

      // Gravity
      this.velY[i] -= this.gravity[i] * dt;

      // Integrate position
      this.posX[i] += this.velX[i] * dt;
      this.posY[i] += this.velY[i] * dt;
      this.posZ[i] += this.velZ[i] * dt;

      // Interpolate size and alpha
      const size  = this.startSize[i]  + (this.endSize[i]  - this.startSize[i])  * t;
      const alpha = this.startAlpha[i] + (this.endAlpha[i] - this.startAlpha[i]) * t;

      // Write to the appropriate mesh's instance buffers
      if (this.isEmissive[i]) {
        const o3 = ai * 3;
        const o2 = ai * 2;
        aOff[o3]     = this.posX[i];
        aOff[o3 + 1] = this.posY[i];
        aOff[o3 + 2] = this.posZ[i];
        aSA[o2]      = size;
        aSA[o2 + 1]  = alpha;
        aTE[o2]      = this.tile[i];
        aTE[o2 + 1]  = this.emissive[i];
        aCol[o3]     = this.colorR[i];
        aCol[o3 + 1] = this.colorG[i];
        aCol[o3 + 2] = this.colorB[i];
        aRot[ai]     = this.rotation[i];
        ai++;
      } else {
        const o3 = ni * 3;
        const o2 = ni * 2;
        nOff[o3]     = this.posX[i];
        nOff[o3 + 1] = this.posY[i];
        nOff[o3 + 2] = this.posZ[i];
        nSA[o2]      = size;
        nSA[o2 + 1]  = alpha;
        nTE[o2]      = this.tile[i];
        nTE[o2 + 1]  = this.emissive[i];
        nCol[o3]     = this.colorR[i];
        nCol[o3 + 1] = this.colorG[i];
        nCol[o3 + 2] = this.colorB[i];
        nRot[ni]     = this.rotation[i];
        ni++;
      }
    }

    // Update instance counts and flag buffers dirty
    nGeo.instanceCount = ni;
    aGeo.instanceCount = ai;

    if (ni > 0) {
      nGeo.getAttribute('aOffset').needsUpdate = true;
      nGeo.getAttribute('aScaleAlpha').needsUpdate = true;
      nGeo.getAttribute('aTileEmissive').needsUpdate = true;
      nGeo.getAttribute('aColor').needsUpdate = true;
      nGeo.getAttribute('aRotation').needsUpdate = true;
    }
    if (ai > 0) {
      aGeo.getAttribute('aOffset').needsUpdate = true;
      aGeo.getAttribute('aScaleAlpha').needsUpdate = true;
      aGeo.getAttribute('aTileEmissive').needsUpdate = true;
      aGeo.getAttribute('aColor').needsUpdate = true;
      aGeo.getAttribute('aRotation').needsUpdate = true;
    }

    this._normalCount = ni;
    this._additiveCount = ai;
  }

  // ── Lighting ────────────────────────────────────────────────────────────────

  /**
   * Update environment lighting uniforms on both particle meshes from a
   * pre-baked HDR sky tint. The bake already encodes the HDR's intrinsic
   * brightness — environmentIntensity is intentionally NOT multiplied in,
   * because the shader uses these as direct color multipliers (not as PBR
   * irradiance via a BRDF), so re-applying the calibration would blow out.
   *
   * effectBrightness is a separate per-scenario dim applied only to the
   * environment-lit (non-emissive) term in the shader — emissive sparks/flares
   * keep their absolute brightness. Used to pull daytime-calibrated smoke/dust
   * back down against the dimmed night scene.
   * @param {{ top: number[], bottom: number[] }} skyTint — linear RGB
   * @param {number} [effectBrightness=1] — env-lit dim multiplier
   */
  setLighting(skyTint, effectBrightness = 1) {
    const t = skyTint?.top ?? [1, 1, 1];
    const b = skyTint?.bottom ?? [0.3, 0.3, 0.3];
    for (const mesh of [this._normalMesh, this._additiveMesh]) {
      mesh.material.uniforms.uSkyTop.value.set(t[0], t[1], t[2]);
      mesh.material.uniforms.uSkyBottom.value.set(b[0], b[1], b[2]);
      mesh.material.uniforms.uEffectBrightness.value = effectBrightness;
    }
  }

  // ── Stats ──────────────────────────────────────────────────────────────────

  get aliveCount() { return this._normalCount + this._additiveCount; }
  get debrisAliveCount() { return this._debrisAliveCount; }

  // ── Cleanup ────────────────────────────────────────────────────────────────

  dispose() {
    this.scene.remove(this._normalMesh);
    this.scene.remove(this._additiveMesh);
    this._normalMesh.geometry.dispose();
    this._additiveMesh.geometry.dispose();
    this._normalMesh.material.dispose();
    this._additiveMesh.material.dispose();
    this._quadGeo.dispose();
    this._atlas.dispose();

    for (const m of this._debrisMeshes) {
      this.scene.remove(m);
      m.dispose();
    }
    for (const g of this._debrisGeos) g.dispose();
    this._debrisMat.dispose();
  }
}
